import React, { ReactNode, ReactElement, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import styled from 'styled-components';
import { Header } from '@tiui/remote.ui.header';
import { Sidebar } from '@tiui/remote.ui.sidebar';
import { ThemeContext } from '@tiui/remote.gfi.providers.theme';
import LinearProgress from '@mui/material/LinearProgress';

type SideBarItem = {
  title: string;
  icon: ReactElement;
  onClick?: (title: string) => void;
};

type AvatarItem = {
  title: string;
  link: string;
  onClick?: () => void;
};
export type AppLayoutProps = {
  /**
   * a node to be rendered in the Home page.
   */
  children?: ReactNode;
  /**
   * The elements to display in the sidebar.
   */
  sideBarItems: SideBarItem[];

  /**
   * @description The index of sidebar item selected initially
   * @default 0
   */
  sideBarInitialSelectedIndex?: number;
  /**
   * The image to use in the header as avatar. A default will be used if not provided
   */
  headerAvatarImage?: ReactNode;
  /**
   * The items that will be diplayed when the Avatar is clicked on.
   * None by default.
   */
  headerAvatarItems?: AvatarItem[];
  /**
   * @description The number of notifications to be displayed on top of the badge
   * @default 0
   */
  notifications?: number;
  /**
   * The logo to be used. Preferably pass a tiui BaseImage component, but any other image will also do.
   */
  logo: ReactNode;
  /**
   * set to true when the page is loading and the layout will display a liner progress bar on the top
   * This is a simple implementation of an indeterminate linear bar. For more complex needs the page should implement the progress indicator itself
   * and leave this false.
   */
  isLoading?: boolean;
};

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: ${(props) => props?.bgColor};
`;

export function AppLayout({
  children,
  logo,
  sideBarItems,
  sideBarInitialSelectedIndex = 0,
  headerAvatarItems,
  headerAvatarImage,
  notifications,
  isLoading=false,
}: AppLayoutProps) {
  const theme = React.useContext(ThemeContext);

  function onClickSidebarItem(
    event: MouseEvent<HTMLElement>,
    index: number
  ): void {
    sideBarItems[index].onClick(sideBarItems[index].title);
  }

  return (
    <>
      {isLoading && <LinearProgress sx={{zIndex: 10000}}/>}
      <Header
        logo={logo}
        notifications={notifications}
        avatarItems={headerAvatarItems}
        avatarImage={headerAvatarImage}
        />
      <PageLayout bgColor={theme?.backgroundDefault}>
        <div style={{ height: '64px', flexShrink: 0, color: '' }} />
        <Sidebar
          items={sideBarItems}
          initialSelectedIndex={sideBarInitialSelectedIndex}
          variant="permanent"
          anchor="left"
          open={true}
          onClick={onClickSidebarItem}
        />

        <Box sx={{ p: 3 }}>
          <div style={{ flex: 1, display: 'flex' }}>
            <div style={{ width: '240px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>{children}</div>
          </div>
        </Box>
      </PageLayout>
    </>
  );
}
